/*
 * Copyright (c) 2024, King Living
 * Password validation message from BM site preference with i18n fallback.
 */

import {isPasswordValid} from '@amblique/king-living/app/utils/password-policy';

export const LOGIN_PASSWORD_ERROR_I18N = {
  defaultMessage: 'Invalid login or password.',
  id: 'login_page.error.incorrect_username_or_password',
};

export const PASSWORD_VALIDATION_MESSAGE_I18N = {
  login: LOGIN_PASSWORD_ERROR_I18N,
  registration: {
    defaultMessage: 'Invalid login or password.',
    id: 'use_registration_fields.error.password_requirements',
  },
  update: {
    defaultMessage: 'Invalid login or password.',
    id: 'use_update_password_fields.error.password_requirements',
  },
  reset: {
    defaultMessage: 'Sorry, the provided password does not match the required constraints.',
    id: 'use_update_password_fields.error.new_password_requirements',
  },
};

const PASSWORD_VALIDATION_PREFERENCE_BY_CONTEXT = {
  login: 'passwordValidationMessage',
  registration: 'passwordValidationMessage',
  update: 'passwordValidationMessage',
  reset: 'newPasswordValidationMessage',
};

/**
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function normalizeBmPasswordMessage(raw) {
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/**
 * @param {string|null|undefined} bmMessage
 * @param {Function} formatMessage
 * @param {{defaultMessage: string, id: string}} i18nDescriptor
 * @returns {string}
 */
export function resolvePasswordValidationMessage(bmMessage, formatMessage, i18nDescriptor) {
  const normalized = normalizeBmPasswordMessage(bmMessage);

  if (normalized) {
    return normalized;
  }

  return formatMessage(i18nDescriptor);
}

/**
 * @param {'login'|'registration'|'update'|'reset'} context
 * @returns {'passwordValidationMessage'|'newPasswordValidationMessage'}
 */
export function getPasswordValidationPreferenceId(context = 'registration') {
  return PASSWORD_VALIDATION_PREFERENCE_BY_CONTEXT[context] || 'passwordValidationMessage';
}

/**
 * @param {string|null|undefined} bmMessage
 * @param {Function} formatMessage
 * @param {'login'|'registration'|'update'|'reset'} context
 * @returns {string}
 */
export function resolvePasswordValidationMessages(bmMessage, formatMessage, context = 'registration') {
  return resolvePasswordValidationMessage(
    bmMessage,
    formatMessage,
    PASSWORD_VALIDATION_MESSAGE_I18N[context] || PASSWORD_VALIDATION_MESSAGE_I18N.registration
  );
}

/**
 * @param {object} options
 * @param {string} options.message
 * @param {object} options.policy - Password policy from SFCC Customer Login Settings
 * @returns {Function} react-hook-form password validator
 */
export function createPasswordValidateRules({message, policy}) {
  return (val) => isPasswordValid(val, policy) || message;
}

/**
 * @param {unknown} error
 * @returns {Promise<Record<string, unknown>>}
 */
export async function getPasswordApiErrorData(error) {
  try {
    return (await error?.response?.json()) || {};
  } catch {
    return {};
  }
}

/**
 * Detects API responses where the password field failed server-side validation.
 *
 * @param {unknown} error
 * @param {Record<string, unknown>} [errorData]
 * @returns {boolean}
 */
export function isPasswordPolicyApiError(error, errorData = {}) {
  if (error?.response?.status !== 400) {
    return false;
  }

  const validationMessages = Array.isArray(errorData?.validationErrors) ?
    errorData.validationErrors.map((entry) => entry?.message || entry?.description).filter(Boolean) :
    [];

  const combinedMessage = [
    error?.message,
    errorData?.message,
    ...validationMessages,
  ].filter(Boolean).join(' ');

  return /password/i.test(combinedMessage);
}
